import { useCallback, useEffect, useMemo, useState } from 'react';
import type { GatewayAgent, SessionRow } from '../types/sessions';
import { STORAGE_KEYS } from '../utils/constants';
import { readJSON, writeJSON } from '../utils/storage';
import { sessionQuickSwitchLabel } from '../utils/sessionDisplay';

const QUICK_SWITCH_STATE_VERSION = 1;

export interface PersistedQuickSwitchSessionState {
  /** Timestamp when the user pinned the session. Null = not pinned. */
  pinnedAt: number | null;
  /** Latest session.updatedAt we have observed from the gateway. */
  lastKnownUpdatedAt: number | null;
  /** Latest activity the user has already handled/visited. */
  lastSeenUpdatedAt: number | null;
  /** Activity marker for pinned sessions that need attention in the rail. */
  lastAttentionUpdatedAt: number | null;
  /** Optional local display name override for the session. */
  customLabel?: string | null;
  /** Optional local spoken aliases used for voice/session switching. */
  spokenAliases?: string[] | null;
  /** Local hide/archive marker for the overview on this device. */
  archivedAt?: number | null;
}

export interface PersistedQuickSwitchGatewayState {
  /** When this gateway's sessions were first baselined locally. */
  bootstrappedAt: number | null;
  /** User-defined quick-switch rail order, session keys from top to bottom. */
  quickSwitchOrder?: string[] | null;
  sessions: Record<string, PersistedQuickSwitchSessionState>;
}

export interface PersistedQuickSwitchState {
  version: number;
  gateways: Record<string, PersistedQuickSwitchGatewayState>;
}

export interface QuickSwitchRailItem {
  sessionKey: string;
  session: SessionRow;
  label: string;
  isPinned: boolean;
  isActive: boolean;
  needsAttention: boolean;
  pinnedAt: number | null;
  attentionAt: number | null;
}

interface ReconcileParams {
  gatewayId: string | null;
  sessions: SessionRow[];
  activeSessionKey: string;
  sessionListReady: boolean;
}

interface SelectItemsParams extends ReconcileParams {
  state: PersistedQuickSwitchState;
  agents?: GatewayAgent[];
}

interface UseSessionQuickSwitchParams extends ReconcileParams {
  agents?: GatewayAgent[];
}

function emptyState(): PersistedQuickSwitchState {
  return {
    version: QUICK_SWITCH_STATE_VERSION,
    gateways: {},
  };
}

function normalizeState(raw: unknown): PersistedQuickSwitchState {
  if (!raw || typeof raw !== 'object') return emptyState();

  const candidate = raw as Partial<PersistedQuickSwitchState>;
  if (candidate.version !== QUICK_SWITCH_STATE_VERSION) return emptyState();
  if (!candidate.gateways || typeof candidate.gateways !== 'object') return emptyState();

  return candidate as PersistedQuickSwitchState;
}

function readQuickSwitchState(): PersistedQuickSwitchState {
  return normalizeState(readJSON(STORAGE_KEYS.SESSION_QUICK_SWITCH, emptyState()));
}

function writeQuickSwitchState(state: PersistedQuickSwitchState): void {
  writeJSON(STORAGE_KEYS.SESSION_QUICK_SWITCH, state);
}

function emptyGatewayState(): PersistedQuickSwitchGatewayState {
  return {
    bootstrappedAt: null,
    quickSwitchOrder: [],
    sessions: {},
  };
}

function cloneGatewayState(gateway: PersistedQuickSwitchGatewayState): PersistedQuickSwitchGatewayState {
  return {
    bootstrappedAt: gateway.bootstrappedAt,
    quickSwitchOrder: Array.isArray(gateway.quickSwitchOrder) ? [...gateway.quickSwitchOrder] : [],
    sessions: { ...gateway.sessions },
  };
}

function cloneSessionState(session: PersistedQuickSwitchSessionState): PersistedQuickSwitchSessionState {
  return { ...session };
}

function createSessionState(updatedAt: number | null): PersistedQuickSwitchSessionState {
  return {
    pinnedAt: null,
    lastKnownUpdatedAt: updatedAt,
    lastSeenUpdatedAt: updatedAt,
    lastAttentionUpdatedAt: null,
    customLabel: null,
    spokenAliases: null,
    archivedAt: null,
  };
}

function normalizeCustomLabel(label: string | null | undefined): string | null {
  const normalized = label?.trim();
  return normalized ? normalized : null;
}

function normalizeSpokenAliasKey(alias: string): string {
  return alias
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/[^\p{L}\p{N} ]+/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function normalizeSpokenAliases(aliases: string[] | null | undefined): string[] | null {
  if (!aliases || aliases.length === 0) return null;

  const seen = new Set<string>();
  const normalized: string[] = [];

  for (const alias of aliases) {
    const cleaned = alias.replace(/\s+/g, ' ').trim();
    if (!cleaned) continue;

    const key = normalizeSpokenAliasKey(cleaned);
    if (!key || seen.has(key)) continue;

    seen.add(key);
    normalized.push(cleaned);
  }

  return normalized.length > 0 ? normalized : null;
}

function sameStringArray(a: string[] | null, b: string[] | null): boolean {
  if (a === b) return true;
  if (!a || !b) return !a && !b;
  if (a.length !== b.length) return false;
  return a.every((value, index) => value === b[index]);
}

function applySessionPreferences(
  session: SessionRow,
  persisted: PersistedQuickSwitchSessionState | undefined,
  options?: {
    includeCustomLabel?: boolean;
    includeSpokenAliases?: boolean;
  },
): SessionRow {
  const includeCustomLabel = options?.includeCustomLabel ?? true;
  const includeSpokenAliases = options?.includeSpokenAliases ?? true;
  const customLabel = includeCustomLabel ? normalizeCustomLabel(persisted?.customLabel) : null;
  const spokenAliases = includeSpokenAliases ? normalizeSpokenAliases(persisted?.spokenAliases) : null;

  if (!customLabel && !spokenAliases) {
    return session;
  }

  return {
    ...session,
    ...(customLabel ? { label: customLabel } : {}),
    ...(spokenAliases ? { spokenAliases } : {}),
  };
}

/**
 * Reconcile persisted quick-switch state with the current gateway session list.
 *
 * The side rail is favorites-only:
 * - pinned sessions are always visible
 * - only pinned inactive sessions can accumulate attention
 * - unpinned sessions are continuously baselined and never surface in the rail
 * - opening a session marks the latest observed activity as handled
 */
export function reconcileQuickSwitchState(
  state: PersistedQuickSwitchState,
  params: ReconcileParams,
): PersistedQuickSwitchState {
  const { gatewayId, sessions, activeSessionKey, sessionListReady } = params;
  if (!gatewayId || !sessionListReady) return state;

  const existingGateway = state.gateways[gatewayId] ?? emptyGatewayState();
  const isBootstrapping = existingGateway.bootstrappedAt == null;

  let nextState = state;
  let nextGateway = existingGateway;
  let stateCloned = false;
  let gatewayCloned = false;

  const ensureStateClone = () => {
    if (!stateCloned) {
      nextState = {
        version: QUICK_SWITCH_STATE_VERSION,
        gateways: { ...state.gateways },
      };
      stateCloned = true;
    }
    if (!gatewayCloned) {
      nextGateway = cloneGatewayState(existingGateway);
      nextState.gateways[gatewayId] = nextGateway;
      gatewayCloned = true;
    }
  };

  if (isBootstrapping) {
    ensureStateClone();
    nextGateway.bootstrappedAt = Date.now();
  }

  const presentSessionKeys = new Set(sessions.map((session) => session.key));

  for (const sessionKey of Object.keys(existingGateway.sessions)) {
    if (presentSessionKeys.has(sessionKey)) continue;
    ensureStateClone();
    delete nextGateway.sessions[sessionKey];
  }

  const existingOrder = Array.isArray(existingGateway.quickSwitchOrder)
    ? existingGateway.quickSwitchOrder
    : [];
  const nextOrder = existingOrder.filter((sessionKey) => presentSessionKeys.has(sessionKey));
  if (nextOrder.length !== existingOrder.length) {
    ensureStateClone();
    nextGateway.quickSwitchOrder = nextOrder;
  }

  for (const session of sessions) {
    const updatedAt = session.updatedAt ?? null;
    const isActive = session.key === activeSessionKey;
    const existingSession = nextGateway.sessions[session.key] ?? existingGateway.sessions[session.key];

    if (!existingSession) {
      ensureStateClone();
      nextGateway.sessions[session.key] = createSessionState(updatedAt);
      continue;
    }

    let nextSession = existingSession;
    let sessionCloned = false;
    const ensureSessionClone = () => {
      if (!gatewayCloned) ensureStateClone();
      if (!sessionCloned) {
        nextSession = cloneSessionState(existingSession);
        nextGateway.sessions[session.key] = nextSession;
        sessionCloned = true;
      }
    };

    if (nextSession.lastKnownUpdatedAt !== updatedAt) {
      ensureSessionClone();
      nextSession.lastKnownUpdatedAt = updatedAt;
    }

    if (isActive) {
      if (nextSession.lastSeenUpdatedAt !== updatedAt) {
        ensureSessionClone();
        nextSession.lastSeenUpdatedAt = updatedAt;
      }
      if (nextSession.lastAttentionUpdatedAt !== null) {
        ensureSessionClone();
        nextSession.lastAttentionUpdatedAt = null;
      }
      if ((nextSession.archivedAt ?? null) !== null) {
        ensureSessionClone();
        nextSession.archivedAt = null;
      }
      continue;
    }

    if (nextSession.pinnedAt == null) {
      if (nextSession.lastSeenUpdatedAt !== updatedAt) {
        ensureSessionClone();
        nextSession.lastSeenUpdatedAt = updatedAt;
      }
      if (nextSession.lastAttentionUpdatedAt !== null) {
        ensureSessionClone();
        nextSession.lastAttentionUpdatedAt = null;
      }
      continue;
    }

    const needsAttention =
      updatedAt != null
      && (nextSession.lastSeenUpdatedAt == null || updatedAt > nextSession.lastSeenUpdatedAt);

    if (needsAttention) {
      if (nextSession.lastAttentionUpdatedAt !== updatedAt) {
        ensureSessionClone();
        nextSession.lastAttentionUpdatedAt = updatedAt;
      }
    } else if (nextSession.lastAttentionUpdatedAt !== null) {
      ensureSessionClone();
      nextSession.lastAttentionUpdatedAt = null;
    }
  }

  return nextState;
}

export function setSessionPinned(
  state: PersistedQuickSwitchState,
  params: { gatewayId: string | null; sessionKey: string; pinned: boolean },
): PersistedQuickSwitchState {
  const { gatewayId, sessionKey, pinned } = params;
  if (!gatewayId) return state;

  const existingGateway = state.gateways[gatewayId] ?? emptyGatewayState();
  const existingSession = existingGateway.sessions[sessionKey] ?? createSessionState(null);
  const existingOrder = Array.isArray(existingGateway.quickSwitchOrder)
    ? existingGateway.quickSwitchOrder
    : [];
  const nextOrder = pinned
    ? existingOrder.includes(sessionKey) ? existingOrder : [...existingOrder, sessionKey]
    : existingOrder.filter((key) => key !== sessionKey);
  const orderChanged = existingOrder.length !== nextOrder.length
    || existingOrder.some((key, index) => key !== nextOrder[index]);

  const nextPinnedAt = pinned ? (existingSession.pinnedAt ?? Date.now()) : null;
  if (existingSession.pinnedAt === nextPinnedAt && !orderChanged) {
    return state;
  }

  return {
    version: QUICK_SWITCH_STATE_VERSION,
    gateways: {
      ...state.gateways,
      [gatewayId]: {
        bootstrappedAt: existingGateway.bootstrappedAt,
        quickSwitchOrder: nextOrder,
        sessions: {
          ...existingGateway.sessions,
          [sessionKey]: {
            ...existingSession,
            pinnedAt: nextPinnedAt,
            lastSeenUpdatedAt: existingSession.lastKnownUpdatedAt,
            lastAttentionUpdatedAt: null,
          },
        },
      },
    },
  };
}

export function setSessionCustomLabel(
  state: PersistedQuickSwitchState,
  params: { gatewayId: string | null; sessionKey: string; label: string | null },
): PersistedQuickSwitchState {
  const { gatewayId, sessionKey, label } = params;
  if (!gatewayId) return state;

  const existingGateway = state.gateways[gatewayId] ?? emptyGatewayState();
  const existingSession = existingGateway.sessions[sessionKey] ?? createSessionState(null);
  const nextLabel = normalizeCustomLabel(label);

  if ((existingSession.customLabel ?? null) === nextLabel) {
    return state;
  }

  return {
    version: QUICK_SWITCH_STATE_VERSION,
    gateways: {
      ...state.gateways,
      [gatewayId]: {
        bootstrappedAt: existingGateway.bootstrappedAt,
        quickSwitchOrder: existingGateway.quickSwitchOrder,
        sessions: {
          ...existingGateway.sessions,
          [sessionKey]: {
            ...existingSession,
            customLabel: nextLabel,
          },
        },
      },
    },
  };
}

export function setSessionSpokenAliases(
  state: PersistedQuickSwitchState,
  params: { gatewayId: string | null; sessionKey: string; aliases: string[] },
): PersistedQuickSwitchState {
  const { gatewayId, sessionKey, aliases } = params;
  if (!gatewayId) return state;

  const existingGateway = state.gateways[gatewayId] ?? emptyGatewayState();
  const existingSession = existingGateway.sessions[sessionKey] ?? createSessionState(null);
  const nextAliases = normalizeSpokenAliases(aliases);
  const currentAliases = normalizeSpokenAliases(existingSession.spokenAliases);

  if (sameStringArray(currentAliases, nextAliases)) {
    return state;
  }

  return {
    version: QUICK_SWITCH_STATE_VERSION,
    gateways: {
      ...state.gateways,
      [gatewayId]: {
        bootstrappedAt: existingGateway.bootstrappedAt,
        quickSwitchOrder: existingGateway.quickSwitchOrder,
        sessions: {
          ...existingGateway.sessions,
          [sessionKey]: {
            ...existingSession,
            spokenAliases: nextAliases,
          },
        },
      },
    },
  };
}

export function hasSessionCustomLabel(
  state: PersistedQuickSwitchState,
  gatewayId: string | null,
  sessionKey: string,
): boolean {
  if (!gatewayId) return false;
  return normalizeCustomLabel(state.gateways[gatewayId]?.sessions[sessionKey]?.customLabel) != null;
}

export function setSessionArchived(
  state: PersistedQuickSwitchState,
  params: { gatewayId: string | null; sessionKey: string; archived: boolean },
): PersistedQuickSwitchState {
  const { gatewayId, sessionKey, archived } = params;
  if (!gatewayId) return state;

  const existingGateway = state.gateways[gatewayId] ?? emptyGatewayState();
  const existingSession = existingGateway.sessions[sessionKey] ?? createSessionState(null);
  const nextArchivedAt = archived ? (existingSession.archivedAt ?? Date.now()) : null;
  const existingOrder = Array.isArray(existingGateway.quickSwitchOrder)
    ? existingGateway.quickSwitchOrder
    : [];
  const nextOrder = archived
    ? existingOrder.filter((key) => key !== sessionKey)
    : existingOrder;
  const orderChanged = existingOrder.length !== nextOrder.length
    || existingOrder.some((key, index) => key !== nextOrder[index]);

  if ((existingSession.archivedAt ?? null) === nextArchivedAt && !orderChanged) {
    return state;
  }

  return {
    version: QUICK_SWITCH_STATE_VERSION,
    gateways: {
      ...state.gateways,
      [gatewayId]: {
        bootstrappedAt: existingGateway.bootstrappedAt,
        quickSwitchOrder: nextOrder,
        sessions: {
          ...existingGateway.sessions,
          [sessionKey]: {
            ...existingSession,
            archivedAt: nextArchivedAt,
            pinnedAt: archived ? null : existingSession.pinnedAt,
            lastAttentionUpdatedAt: null,
          },
        },
      },
    },
  };
}

export function setQuickSwitchOrder(
  state: PersistedQuickSwitchState,
  params: { gatewayId: string | null; orderedSessionKeys: string[] },
): PersistedQuickSwitchState {
  const { gatewayId, orderedSessionKeys } = params;
  if (!gatewayId) return state;

  const existingGateway = state.gateways[gatewayId] ?? emptyGatewayState();
  const seen = new Set<string>();
  const nextOrder = orderedSessionKeys.filter((sessionKey) => {
    if (seen.has(sessionKey)) return false;
    const persisted = existingGateway.sessions[sessionKey];
    if (!persisted || persisted.pinnedAt == null || (persisted.archivedAt ?? null) != null) return false;
    seen.add(sessionKey);
    return true;
  });
  const existingOrder = Array.isArray(existingGateway.quickSwitchOrder)
    ? existingGateway.quickSwitchOrder.filter((sessionKey) => existingGateway.sessions[sessionKey]?.pinnedAt != null)
    : [];

  const sameOrder = existingOrder.length === nextOrder.length
    && existingOrder.every((sessionKey, index) => sessionKey === nextOrder[index]);

  if (sameOrder) return state;

  return {
    version: QUICK_SWITCH_STATE_VERSION,
    gateways: {
      ...state.gateways,
      [gatewayId]: {
        bootstrappedAt: existingGateway.bootstrappedAt,
        quickSwitchOrder: nextOrder,
        sessions: existingGateway.sessions,
      },
    },
  };
}

export function isSessionPinned(
  state: PersistedQuickSwitchState,
  gatewayId: string | null,
  sessionKey: string,
): boolean {
  if (!gatewayId) return false;
  return state.gateways[gatewayId]?.sessions[sessionKey]?.pinnedAt != null;
}

export function getPinnedSessionKeys(
  state: PersistedQuickSwitchState,
  gatewayId: string | null,
): Set<string> {
  if (!gatewayId) return new Set();
  const gateway = state.gateways[gatewayId];
  if (!gateway) return new Set();

  return new Set(
    Object.entries(gateway.sessions)
      .filter(([, session]) => session.pinnedAt != null && (session.archivedAt ?? null) == null)
      .map(([sessionKey]) => sessionKey),
  );
}

export function isSessionArchived(
  state: PersistedQuickSwitchState,
  gatewayId: string | null,
  sessionKey: string,
): boolean {
  if (!gatewayId) return false;
  return (state.gateways[gatewayId]?.sessions[sessionKey]?.archivedAt ?? null) != null;
}

export function applySessionOverviewPreferences(
  state: PersistedQuickSwitchState,
  gatewayId: string | null,
  sessions: SessionRow[],
): SessionRow[] {
  if (!gatewayId || sessions.length === 0) return sessions;

  const gateway = state.gateways[gatewayId];
  if (!gateway) return sessions;

  return sessions
    .filter((session) => (gateway.sessions[session.key]?.archivedAt ?? null) == null)
    .map((session) => applySessionPreferences(session, gateway.sessions[session.key]));
}

export function applySessionVoicePreferences(
  state: PersistedQuickSwitchState,
  gatewayId: string | null,
  sessions: SessionRow[],
): SessionRow[] {
  if (!gatewayId || sessions.length === 0) return sessions;

  const gateway = state.gateways[gatewayId];
  if (!gateway) return sessions;

  return sessions.map((session) => applySessionPreferences(session, gateway.sessions[session.key], {
    includeCustomLabel: false,
    includeSpokenAliases: true,
  }));
}

function resolveAgentDisplayName(sessionKey: string, agents: GatewayAgent[] | undefined): string | undefined {
  if (!agents || agents.length === 0) return undefined;

  const parts = sessionKey.split(':');
  if (parts.length < 2 || parts[0] !== 'agent') return undefined;

  const agentId = parts[1];
  const agent = agents.find((entry) => entry.id === agentId);
  return agent?.identity?.name ?? agent?.name;
}

export function selectQuickSwitchItems(params: SelectItemsParams): QuickSwitchRailItem[] {
  const { state, gatewayId, sessions, activeSessionKey, agents } = params;
  if (!gatewayId || sessions.length === 0) return [];

  const gateway = state.gateways[gatewayId];
  if (!gateway) return [];

  const items = sessions.reduce<QuickSwitchRailItem[]>((acc, session) => {
    const persisted = gateway.sessions[session.key];
    if (!persisted || persisted.pinnedAt == null || (persisted.archivedAt ?? null) != null) return acc;

    const displaySession = applySessionPreferences(session, persisted);
    acc.push({
      sessionKey: session.key,
      session: displaySession,
      label: sessionQuickSwitchLabel(displaySession, resolveAgentDisplayName(session.key, agents)),
      isPinned: true,
      isActive: session.key === activeSessionKey,
      needsAttention: persisted.lastAttentionUpdatedAt != null,
      pinnedAt: persisted.pinnedAt,
      attentionAt: persisted.lastAttentionUpdatedAt,
    });

    return acc;
  }, []);

  const order = Array.isArray(gateway.quickSwitchOrder) ? gateway.quickSwitchOrder : [];
  const orderIndex = new Map(order.map((sessionKey, index) => [sessionKey, index]));

  items.sort((a, b) => {
    const aIndex = orderIndex.get(a.sessionKey);
    const bIndex = orderIndex.get(b.sessionKey);

    if (aIndex != null && bIndex != null) return aIndex - bIndex;
    if (aIndex != null) return -1;
    if (bIndex != null) return 1;

    return (a.pinnedAt ?? 0) - (b.pinnedAt ?? 0);
  });

  return items;
}

export function useSessionQuickSwitch(params: UseSessionQuickSwitchParams) {
  const {
    gatewayId,
    sessions,
    activeSessionKey,
    sessionListReady,
    agents,
  } = params;
  const [persistedState, setPersistedState] = useState<PersistedQuickSwitchState>(() => readQuickSwitchState());

  // Reconcile during render (copy-on-write means identity === means no change).
  // Calling setPersistedState during render is the React-recommended pattern
  // for computing derived state — it schedules an immediate re-render before
  // the browser paints, so no stale frame is shown.
  const state = reconcileQuickSwitchState(persistedState, {
    gatewayId,
    sessions,
    activeSessionKey,
    sessionListReady,
  });
  if (state !== persistedState) {
    setPersistedState(state);
  }

  useEffect(() => {
    writeQuickSwitchState(state);
  }, [state]);

  const setPinned = useCallback((sessionKey: string, pinned: boolean) => {
    const next = setSessionPinned(state, { gatewayId, sessionKey, pinned });
    if (next !== state) {
      writeQuickSwitchState(next);
      setPersistedState(next);
    }
  }, [state, gatewayId]);

  const togglePinned = useCallback((sessionKey: string) => {
    const next = setSessionPinned(state, {
      gatewayId,
      sessionKey,
      pinned: !isSessionPinned(state, gatewayId, sessionKey),
    });
    if (next !== state) {
      writeQuickSwitchState(next);
      setPersistedState(next);
    }
  }, [state, gatewayId]);

  const items = useMemo(() => selectQuickSwitchItems({
    state,
    gatewayId,
    sessions,
    activeSessionKey,
    sessionListReady,
    agents,
  }), [state, gatewayId, sessions, activeSessionKey, sessionListReady, agents]);

  const visibleSessions = useMemo(
    () => applySessionOverviewPreferences(state, gatewayId, sessions),
    [state, gatewayId, sessions],
  );

  const voiceRouteSessions = useMemo(
    () => applySessionVoicePreferences(state, gatewayId, sessions),
    [state, gatewayId, sessions],
  );

  const pinnedSessionKeys = useMemo(
    () => getPinnedSessionKeys(state, gatewayId),
    [state, gatewayId],
  );

  const isPinned = useCallback((sessionKey: string) => {
    return isSessionPinned(state, gatewayId, sessionKey);
  }, [state, gatewayId]);

  const renameSession = useCallback((sessionKey: string, label: string | null) => {
    const next = setSessionCustomLabel(state, { gatewayId, sessionKey, label });
    if (next !== state) {
      writeQuickSwitchState(next);
      setPersistedState(next);
    }
  }, [state, gatewayId]);

  const setSpokenAliases = useCallback((sessionKey: string, aliases: string[]) => {
    const next = setSessionSpokenAliases(state, { gatewayId, sessionKey, aliases });
    if (next !== state) {
      writeQuickSwitchState(next);
      setPersistedState(next);
    }
  }, [state, gatewayId]);

  const archiveSession = useCallback((sessionKey: string, archived: boolean) => {
    const next = setSessionArchived(state, { gatewayId, sessionKey, archived });
    if (next !== state) {
      writeQuickSwitchState(next);
      setPersistedState(next);
    }
  }, [state, gatewayId]);

  const reorderItems = useCallback((orderedSessionKeys: string[]) => {
    const next = setQuickSwitchOrder(state, { gatewayId, orderedSessionKeys });
    if (next !== state) {
      writeQuickSwitchState(next);
      setPersistedState(next);
    }
  }, [state, gatewayId]);

  const isArchived = useCallback((sessionKey: string) => {
    return isSessionArchived(state, gatewayId, sessionKey);
  }, [state, gatewayId]);

  const hasLocalCustomLabel = useCallback((sessionKey: string) => {
    return hasSessionCustomLabel(state, gatewayId, sessionKey);
  }, [state, gatewayId]);

  return {
    items,
    visibleSessions,
    voiceRouteSessions,
    pinnedSessionKeys,
    isPinned,
    isArchived,
    hasLocalCustomLabel,
    setPinned,
    togglePinned,
    renameSession,
    setSpokenAliases,
    archiveSession,
    reorderItems,
  };
}
